import { useEffect, useMemo, useState } from "react";
import type { PickupLocationPublic, PickupOptionsPublic } from "@gaole/shared";
import {
  listAvailablePickupDays,
  listPickupSlotsForDay,
  formatPickupScheduledAt,
  PICKUP_WEEKDAY_LABELS,
  pickupCalendarDayAdd,
  pickupScheduleBounds,
  pickupUtcToParts,
  samePickupCalendarDay,
  startOfPickupCalendarDay,
} from "@gaole/shared";

function formatDayChip(day: Date, now: Date): string {
  const today = startOfPickupCalendarDay(now);
  const tomorrow = pickupCalendarDayAdd(today, 1);
  if (samePickupCalendarDay(day, today)) return "Today";
  if (samePickupCalendarDay(day, tomorrow)) return "Tomorrow";
  const p = pickupUtcToParts(day);
  const month = new Date(Date.UTC(p.year, p.month - 1, p.day)).toLocaleDateString(undefined, {
    month: "short",
    timeZone: "UTC",
  });
  return `${PICKUP_WEEKDAY_LABELS[p.weekday]}, ${month} ${p.day}`;
}

function formatSlotTime(d: Date): string {
  const p = pickupUtcToParts(d);
  const hour = Math.floor(p.minutes / 60);
  const minute = p.minutes % 60;
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${ampm}`;
}

export function PickupSchedulePicker({
  options,
  location,
  value,
  onChange,
}: {
  options: PickupOptionsPublic;
  location: PickupLocationPublic;
  value: string;
  onChange: (iso: string) => void;
}) {
  const now = useMemo(() => new Date(), []);
  const { minAt, maxAt } = useMemo(
    () => pickupScheduleBounds(now, options.minLeadHours, options.maxDaysAhead),
    [now, options.minLeadHours, options.maxDaysAhead],
  );

  const availableDays = useMemo(
    () =>
      listAvailablePickupDays(
        now,
        options.minLeadHours,
        options.maxDaysAhead,
        location.availability,
      ),
    [now, options.minLeadHours, options.maxDaysAhead, location.availability],
  );

  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  useEffect(() => {
    setSelectedDay(null);
  }, [location.id]);

  useEffect(() => {
    if (!availableDays.length) {
      if (value) onChange("");
      return;
    }

    const current = value ? new Date(value) : null;
    const validCurrent =
      current &&
      !Number.isNaN(current.getTime()) &&
      availableDays.some((d) => samePickupCalendarDay(d, current)) &&
      listPickupSlotsForDay(current, location.availability, minAt, maxAt).some(
        (s) => s.getTime() === current.getTime(),
      );

    if (validCurrent) {
      setSelectedDay(startOfPickupCalendarDay(current));
      return;
    }

    const firstDay = availableDays[0];
    const firstSlots = listPickupSlotsForDay(firstDay, location.availability, minAt, maxAt);
    const nextIso = firstSlots[0]?.toISOString() ?? "";
    setSelectedDay(firstDay);
    if (nextIso !== value) onChange(nextIso);
  }, [availableDays, location.id, location.availability, minAt, maxAt, value, onChange]);

  const activeDay = selectedDay ?? availableDays[0] ?? null;
  const slots = activeDay
    ? listPickupSlotsForDay(activeDay, location.availability, minAt, maxAt)
    : [];

  const selectedDate = value ? new Date(value) : null;

  if (!availableDays.length) {
    return (
      <p className="text-xs text-white/50">
        No pickup slots available in the next {options.maxDaysAhead} day
        {options.maxDaysAhead === 1 ? "" : "s"} for this location.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="mb-1.5 text-xs text-white/50">Preferred date</p>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {availableDays.map((day) => {
            const active = activeDay && samePickupCalendarDay(day, activeDay);
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => {
                  setSelectedDay(day);
                  const daySlots = listPickupSlotsForDay(day, location.availability, minAt, maxAt);
                  if (daySlots[0]) onChange(daySlots[0].toISOString());
                }}
                className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  active
                    ? "border-brand-pink bg-brand-pink/15 text-brand-pink"
                    : "border-white/10 bg-surface text-white/60 hover:text-white"
                }`}
              >
                {formatDayChip(day, now)}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="mb-1.5 text-xs text-white/50">Preferred time</p>
        {slots.length === 0 ? (
          <p className="text-xs text-white/50">No times available on this day.</p>
        ) : (
          <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4">
            {slots.map((slot) => {
              const active =
                selectedDate &&
                !Number.isNaN(selectedDate.getTime()) &&
                slot.getTime() === selectedDate.getTime();
              return (
                <button
                  key={slot.toISOString()}
                  type="button"
                  onClick={() => onChange(slot.toISOString())}
                  className={`rounded-xl border px-2 py-2 text-xs font-semibold transition-colors ${
                    active
                      ? "border-brand-pink bg-brand-pink/15 text-brand-pink"
                      : "border-white/10 bg-surface text-white/70 hover:border-white/20"
                  }`}
                >
                  {formatSlotTime(slot)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {value ? (
        <div className="rounded-xl border border-brand-pink/35 bg-brand-pink/10 px-3 py-2.5 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-brand-pink/90">
            Your pickup slot
          </p>
          <p className="font-display text-sm font-bold text-white">{formatPickupScheduledAt(value)}</p>
        </div>
      ) : null}
    </div>
  );
}
